// web/src/i18n/useLanguage.ts
//
// Tiny hook exposing the active language + RTL flag + setter. Used by
// any component that needs to render locale-aware content (formatters,
// calendar pickers, the language picker, etc.).
import { createContext, useContext } from "react";

export type LanguageContextValue = {
  language: string;
  rtl: boolean;
  setLanguage: (code: string) => void | Promise<void>;
};

export const LanguageContext = createContext<LanguageContextValue | null>(null);

/**
 * Returns the active language descriptor. When no <LanguageProvider> is in
 * scope (e.g. test renders that don't bother wrapping), returns a safe
 * default that mirrors what the provider would have produced — language
 * "en", rtl=false, no-op setter. The LanguageProvider context still
 * supersedes this for any subtree that mounts it.
 *
 * Falling back rather than throwing keeps the helper composable with
 * components that may render outside the authenticated shell (e.g. small
 * unit tests for calendar widgets) while preserving the same shape.
 */
export function useLanguage(): LanguageContextValue {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    return {
      language: "en",
      rtl: false,
      setLanguage: () => {},
    };
  }
  return ctx;
}
